import {USER_KEYS} from "@/api/userApi";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {useSelectedCompany} from "@/hooks/useUserHooks";
import {useCreateAgencyStore} from "@/store/createAgency";
import {
  hasClosedPaymentFailedModalAtom,
  isFinishRegistrationModalVisibleAtom,
  isWelcomeToMultiAccountModalOpenAtom,
  paymentFailedModalDataAtom,
  selectedCompanyIdAtom,
} from "@/store/jotaiAtoms";
import useTokenStore from "@/store/token";
import type {TUserState} from "@/store/types";
import useUserStore from "@/store/user";
import {useAtomValue, useSetAtom} from "jotai";
import {useEffect} from "react";

const useFetchUser = () => {
  const userId = useTokenStore(state => state.userId);
  const user = useUserStore(state => state.user);
  const selectedCompany = useSelectedCompany();
  const setSelectedCompanyId = useSetAtom(selectedCompanyIdAtom);
  const setUserData = useUserStore(state => state.setUserData);
  const hasClosedPaymentFailedModal = useAtomValue(hasClosedPaymentFailedModalAtom);
  const setAgencyValue = useCreateAgencyStore(state => state.setValue);
  const setPaymentFailedModalData = useSetAtom(paymentFailedModalDataAtom);
  const setIsFinishRegistrationModalVisible = useSetAtom(isFinishRegistrationModalVisibleAtom);
  const setIsWelcomeToMultiAccountModalVisible = useSetAtom(isWelcomeToMultiAccountModalOpenAtom);

  const {refetch, data, error} = useGenericQuery<TUserState>({
    queryKey: USER_KEYS.qUser(userId!),
    enabled: !!userId,
  });

  useEffect(() => {
    if (data) {
      setUserData(data);

      if (data.user_access_level === "INFLUENCER_ADMIN" || data.user_access_level === "COMPANY_ADMIN") {
        setAgencyValue("form.sales_email", data.email!);
      }

      const isAgency = data.user_access_level === "AGENCY" || data.agencies?.[0]?.agency.multiple_brands;
      const isBrand =
        !isAgency && (data.user_access_level === "SINGLE_BRAND" || data.user_access_level === "SUB_BRAND");

      if (!selectedCompany && !isAgency && data.user_access_level !== "INFLUENCER_ADMIN") {
        setSelectedCompanyId(data.sub_brands?.[0]?.company.id!);
      }

      setIsFinishRegistrationModalVisible(isBrand && !selectedCompany?.interests?.length);
      setIsWelcomeToMultiAccountModalVisible(!!isAgency && !data.agencies?.[0]?.agency?.filled_out_agency_data);
    }
  }, [data, setUserData, setIsFinishRegistrationModalVisible, selectedCompany]);

  useEffect(() => {
    const failedInvoices = user.payment_failed_invoices;

    if (failedInvoices?.length && !hasClosedPaymentFailedModal) {
      setPaymentFailedModalData(failedInvoices);
    }
  }, [user.payment_failed_invoices, hasClosedPaymentFailedModal]);

  return {
    refetch,
    userId,
    error,
  };
};

export {useFetchUser};
